import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiBuildingLine, RiCheckboxCircleLine, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import companyService, { Company, CompanyForm } from '../../services/companyService';
import userService from '../../services/userService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useCreateCompany, useUpdateCompany } from '../../hooks/mutations/useCompanyMutations';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { COMPANIES_PATH, companyDetailUrl } from '../../utils/companyLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CompanyFormFields from '../../components/company/CompanyFormFields';
import {
  emptyCompanyForm,
  companyToForm,
  companyUpdatePayload,
  validateCompanyForm,
  companyIfMatch,
  COMPANY_FIELD_LABELS,
} from '../../components/company/companyForm';
import {
  DetailPageLayout,
  DetailSection,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
} from '../../components/detail';

const LIST_PATH = COMPANIES_PATH;

/**
 * หน้าเพิ่ม/แก้ไขบริษัท — `/master-data/companies/new` · `/master-data/companies/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน CompanyPage · มติ PO 28 ต.ค. 2026 · หน้าตาแบบ MaintenanceFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSaveCompany ของโมดัลเดิมครบ: กันกดซ้ำ (ref) · รหัสซ้ำ → สร้างรหัสใหม่แล้วให้กดอีกครั้ง ·
 * If-Match กันแก้ทับ (409) · ชื่อซ้ำ → error ใต้ช่องชื่อ · 422 field_errors → ใต้ช่อง · ที่เหลือ → alert
 */
const CompanyFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const companyId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  const { backTo, goTo } = useDetailNavigation(LIST_PATH);
  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany();

  const [formData, setFormData] = useState<CompanyForm>(() => emptyCompanyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState<string>('');
  /** ค่าตั้งต้นของฟอร์ม (JSON) — เทียบเพื่อรู้ว่าผู้ใช้แก้แล้วหรือยัง · null = ยังโหลดไม่เสร็จ */
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const companyQuery = useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? 0),
    queryFn: () => companyService.getCompany(companyId!),
    enabled: isEdit && companyId !== null,
    // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const company: Company | undefined = companyQuery.data;

  // รายชื่อพนักงานขาย (gate companies.view ที่ backend — role ที่มีแค่ create ได้รายการว่าง ไม่พังหน้า)
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'sales-persons'],
    queryFn: () => userService.getSalesPersons(),
    staleTime: 5 * 60 * 1000,
  });

  // ตั้งค่าฟอร์มครั้งเดียวต่อบริษัท (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์)
  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!company || initializedFor.current === company.id) return;
      initializedFor.current = company.id;
      const f = companyToForm(company);
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง (รหัสจริงออกตอนบันทึก)
    codeGeneratorService
      .getNextCodePreview('company', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting next code preview:', error);
        return ''; // fallback to empty
      })
      .then((code) => {
        const f = emptyCompanyForm(code);
        setFormData(f);
        setInitialSnapshot(JSON.stringify(f));
      });
  }, [isEdit, company]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const viewUrl = companyDetailUrl;
  const cancelTo = isEdit && companyId ? viewUrl(companyId) : backTo;

  const isSubmittingRef = useRef(false); // BUG-S802 double-submit guard
  const saving = createCompanyMutation.isPending || updateCompanyMutation.isPending;

  const handleSave = async () => {
    // BUG-S802: synchronous guard กัน rapid double-click ยิง mutateAsync ซ้ำ same-tick
    if (isSubmittingRef.current) return;

    const errors = validateCompanyForm(formData, isEdit ? 'edit' : 'create');
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to first error field after state update
      setTimeout(() => {
        const el = document.querySelector(`[name="${Object.keys(errors)[0]}"]`) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }
    setFormErrors({});

    isSubmittingRef.current = true;
    try {
      let savedCompany: Company;
      if (!isEdit) {
        const dataToSave = { ...formData };
        // ตรวจสอบว่ารหัสซ้ำหรือไม่ — ซ้ำ = สร้างรหัสใหม่ แสดงเตือนใต้ช่อง รอผู้ใช้กดบันทึกอีกครั้ง (ไม่บันทึกอัตโนมัติ)
        if (await companyService.checkCodeExists(dataToSave.code)) {
          const oldCode = dataToSave.code;
          try {
            const newCode = await codeGeneratorService.generateSingleCode('company');
            setFormData((prev) => ({ ...prev, code: newCode }));
            setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
          } catch (genError) {
            console.error('Error generating new code:', genError);
            setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
          }
          return;
        }
        setCodeError('');
        savedCompany = await createCompanyMutation.mutateAsync(dataToSave);
      } else {
        if (!company) return;
        savedCompany = await updateCompanyMutation.mutateAsync({
          id: company.id,
          data: companyUpdatePayload(formData, initialSnapshot ? (JSON.parse(initialSnapshot) as CompanyForm) : null),
          // P-S468-1: แนบ updated_at ที่โหลดมา → server ตรวจ 409 กันแก้ทับกันเงียบ
          ifMatch: companyIfMatch(company),
        });
      }
      setSaved(true); // ปลดตัวกันออกจากหน้า ก่อนเปลี่ยนหน้า
      const savedId = savedCompany?.id ?? company?.id;
      // ไม่มีสิทธิ์ดู = กลับหน้ารายการ (ซึ่งก็ต้อง view) → ถอยไปที่เปิดมา
      if (savedId && hasPermission('companies.view')) goTo(viewUrl(savedId), { replace: true });
      else goTo(backTo, { replace: true });
    } catch (error: any) {
      console.error('Error saving company:', error);

      // P-S601-1: 409 stale-edit — มีคนแก้บริษัทนี้ไปก่อน (useMutationWithRefresh แจ้งเตือนข้อความไทยให้แล้ว)
      // โหลดค่าล่าสุดจาก server ลงฟอร์มใหม่ (ได้ token ใหม่ด้วย) — ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำด้วย token ใหม่
      // เพราะเท่ากับเขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร (ด่าน If-Match ไร้ความหมาย)
      if (error.response?.status === 409) {
        initializedFor.current = null;
        companyQuery.refetch();
        return;
      }

      // OBS-S1109-001 FIX (S1113): BUSINESS_RULE_VIOLATION on name dup → inline field error
      const businessRuleMessage = error.response?.data?.error?.user_message ||
                                  error.response?.data?.error?.message || '';
      if (businessRuleMessage.includes('ชื่อ') && businessRuleMessage.includes('มีอยู่ในระบบแล้ว')) {
        setFormErrors((prev) => ({ ...prev, name: businessRuleMessage }));
        const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
        if (nameEl) nameEl.focus();
        return;
      }

      // BUG-S1162-001: Map server 422 field_errors → inline (WCAG 3.3.1)
      const fieldErrMap = mapFieldErrors(error);
      if (fieldErrMap) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrMap }));
        focusFirstInvalidField(fieldErrMap);
        return;
      }

      let errorMessage = 'ไม่สามารถบันทึกข้อมูลบริษัทได้ กรุณาลองใหม่อีกครั้ง';
      const data = error.response?.data;
      if (data) {
        if (typeof data.detail === 'string') errorMessage = data.detail;
        else if (Array.isArray(data.detail)) errorMessage = data.detail.map((e: any) => e.msg || e.message).join(', ');
        else if (data.error?.user_message) errorMessage = data.error.user_message;
        else if (data.message) errorMessage = data.message;
      }
      showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
    } finally {
      isSubmittingRef.current = false; // BUG-S802: reset ทุก exit path
    }
  };

  const title = isEdit ? 'แก้ไขข้อมูลบริษัท' : 'เพิ่มบริษัทใหม่';
  const breadcrumbs = useMemo(() => [
    { label: 'ข้อมูลบริษัท', path: backTo },
    ...(isEdit && company ? [{ label: company.name, path: viewUrl(company.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, company]);

  const notFound = isEdit && companyId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiBuildingLine />}
      title={title}
      recordId={isEdit ? company?.code || companyId : undefined}
      subtitle={isEdit ? company?.name : 'กรอกข้อมูลบริษัทลูกค้า (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && companyQuery.isLoading) || (initialSnapshot === null && !notFound && !companyQuery.isError)}
      error={notFound || companyQuery.error}
      notFoundMessage="ไม่พบบริษัทที่ระบุ"
      onRetry={() => companyQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && company ? (
          <>
            <RecordMetaCard
              createdAt={company.createdAt}
              createdBy={company.created_by}
              updatedAt={company.updatedAt}
              updatedBy={company.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline entityType="company" entityId={company.id} fieldLabels={COMPANY_FIELD_LABELS} />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลบริษัท" icon={<RiBuildingLine />}>
        {/* ไม่ใช้ <form onSubmit>: Enter ในช่องค้นหาของ SearchableSelect จะกลายเป็นกดบันทึก (โมดัลเดิมก็ไม่ใช้ native submit) */}
        <div>
          <CompanyFormFields
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
            setCodeError={setCodeError}
            users={users}
            currentSalesPerson={company?.sales_person}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default CompanyFormPage;
